import { Controller } from '@hotwired/stimulus';
import { DirectUpload } from '@rails/activestorage';
import { track } from '../lib/analytics';

// One uploader to drive every recipe-engine tool (GIF, mute, rotate, frame,
// merge, resize, volume, speed). Single or multi file; tools without an
// options step auto-submit as soon as the upload lands.
export default class extends Controller {
  static values = {
    genericErrorMessage: String,
    needsTwoFilesMessage: String,
    fileTooLargeMessage: String,
    maxBytes: Number,
    directUploadUrl: String,
    multiple: Boolean,
    autoSubmit: Boolean,
    tool: String,
  };

  static targets = [
    'uploadError',
    'uploadErrorMessage',
    'progressBarWrapper',
    'progressBar',
    'progressPercentage',
    'input',
    'uploadFormWrapper',
    'uploadFormContent',
    'dropOverlay',
    'fileCard',
    'fileTile',
    'filename',
    'size',
    'optionsStep',
    'workingWrapper',
    'finalStep',
    'submitButton',
  ];

  connect() {
    if (this.hasInputTarget) {
      this.inputTarget.value = null;
      this.element.addEventListener('dragenter', this.onDragEnter.bind(this));
      this.element.addEventListener('dragleave', this.onDragExit.bind(this));
      this.element.addEventListener('dragover', this.onDragOver.bind(this));
      this.element.addEventListener('drop', this.onDrop.bind(this));
      this.uploadFormContentTarget.addEventListener('click', this.onContainerClick.bind(this));
    }

    this.uploading = false;
    this.createdHiddenInputs = [];
    this.activeXHRs = [];
    this.hide('optionsStep');
    this.hide('workingWrapper');
    this.hide('finalStep');
  }

  isFileUploadEnabled() {
    return !this.uploading && !this.uploadFormWrapperTarget.classList.contains('hidden');
  }

  onDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isFileUploadEnabled()) return;

    track(`Tool ${this.toolValue} file dropped`);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      this.handleFiles(files);
      this.dropOverlayTarget.classList.remove('opacity-100');
      this.dropOverlayTarget.classList.add('opacity-0', 'pointer-events-none');
    }
  }

  onDragEnter(event) {
    event.preventDefault();
    if (!this.isFileUploadEnabled()) return;
    if (event.dataTransfer.types.includes('Files')) {
      this.dropOverlayTarget.classList.add('opacity-100');
      this.dropOverlayTarget.classList.remove('opacity-0', 'pointer-events-none');
    }
  }

  onDragExit(event) {
    if (!this.isFileUploadEnabled()) return;
    if (event.currentTarget === this.element && !this.element.contains(event.relatedTarget)) {
      this.dropOverlayTarget.classList.remove('opacity-100');
      this.dropOverlayTarget.classList.add('opacity-0', 'pointer-events-none');
    }
  }

  onDragOver(event) {
    if (!this.isFileUploadEnabled()) return;
    event.preventDefault();
  }

  onContainerClick(event) {
    if (!this.isFileUploadEnabled()) return;
    if (event.target === this.inputTarget || event.target.id === 'label-file') return;
    this.inputTarget.click();
  }

  start() {
    this.handleFiles(Array.from(this.inputTarget.files));
  }

  handleFiles(files) {
    if (files.length === 0) return;
    if (!this.multipleValue) files = files.slice(0, 1);
    // Merge submits right after upload, and the server rejects fewer than two
    // files. Catch it here with a helpful message instead of a failed submit.
    if (this.multipleValue && files.length < 2) {
      this.showInlineError(this.needsTwoFilesMessageValue || this.genericErrorMessageValue);
      return;
    }

    if (this.maxBytesValue && files.some((file) => file.size > this.maxBytesValue)) {
      this.showInlineError(this.fileTooLargeMessageValue || this.genericErrorMessageValue);
      return;
    }

    track(`Tool ${this.toolValue} upload started`, { count: files.length });
    this.uploading = true;
    this.showProgress(files);

    let completed = 0;
    files.forEach((file) => {
      // Create the hidden input NOW, in selection order: uploads finish in
      // whatever order the network decides, and merge order must match what
      // the user picked, not which file was smallest.
      const hidden = this.createHiddenInput(this.multipleValue ? 'files[]' : 'file', '');
      const upload = new DirectUpload(file, this.directUploadUrlValue, this);
      upload.create((error, blob) => {
        if (error) {
          this.handleUploadError(error);
          return;
        }
        hidden.value = blob.signed_id;
        completed += 1;
        const percentage = Math.floor((completed / files.length) * 100);
        this.progressBarTarget.style.width = `${percentage}%`;
        this.progressPercentageTarget.textContent = `${percentage}%`;

        if (completed === files.length) {
          track(`Tool ${this.toolValue} upload finished`, { count: files.length });
          this.uploading = false;
          this.hide('progressBarWrapper');
    this.markFileTileReady();
          if (this.autoSubmitValue || !this.hasOptionsStepTarget) {
            this.submitOptions();
          } else {
            this.show('optionsStep');
          }
        }
      });
    });
  }

  showProgress(files) {
    this.hide('uploadFormContent');
    this.show('progressBarWrapper');
    this.show('fileCard');
    this.hide('optionsStep');
    this.hide('workingWrapper');
    this.hide('finalStep');
    this.markFileTilePending();
    this.filenameTarget.textContent = files.length === 1 ? files[0].name : `${files.length} files`;
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    this.sizeTarget.textContent = this.prettyPrintSize(totalBytes);
    this.progressBarTarget.style.width = '0%';
    this.progressPercentageTarget.textContent = '0%';
    this.scrollToTop();
  }

  // Fires on the options-step button (or directly when auto-submitting).
  submitOptions() {
    this.hide('optionsStep');
    this.show('workingWrapper');
    const form = this.element.querySelector('form');
    if (form) form.requestSubmit();
  }

  showFinalStep() {
    this.hide('optionsStep');
    this.hide('workingWrapper');
    this.show('finalStep');
  }

  deleteFile(event) {
    this.hide('uploadError');
    const stage = document.getElementById('progress_button');
    if (stage) stage.innerHTML = '';
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.uploading = false;
    this.activeXHRs.forEach((xhr) => xhr.abort());
    this.activeXHRs = [];
    this.createdHiddenInputs.forEach((input) => input.remove());
    this.createdHiddenInputs = [];
    if (this.hasInputTarget) this.inputTarget.value = null;
    this.filenameTarget.textContent = '';
    this.sizeTarget.textContent = '';
    this.hide('progressBarWrapper');
    this.hide('optionsStep');
    this.hide('workingWrapper');
    this.hide('finalStep');
    this.hide('fileCard');
    this.show('uploadFormContent');
    track(`Tool ${this.toolValue} upload cancelled`);
  }

  createHiddenInput(name, value) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('value', value);
    hiddenField.name = name;
    const form = this.element.querySelector('form');
    (form || this.element).appendChild(hiddenField);
    this.createdHiddenInputs.push(hiddenField);
    return hiddenField;
  }

  directUploadWillStoreFileWithXHR(request) {
    this.activeXHRs.push(request);
    request.addEventListener('error', (event) => this.handleUploadError(event));
  }

  handleUploadError(error) {
    console.error('Upload error:', error);
    this.showInlineError(this.genericErrorMessageValue);
  }

  showInlineError(message) {
    this.resetToStart();
    if (this.hasUploadErrorMessageTarget) this.uploadErrorMessageTarget.textContent = message;
    this.show('uploadError');
  }

  // Reset the flow without hiding the error banner (deleteFile clears it).
  resetToStart() {
    const saved = this.hasUploadErrorTarget && !this.uploadErrorTarget.classList.contains('hidden');
    this.deleteFile();
    if (saved) this.show('uploadError');
  }

  prettyPrintSize(size) {
    const mb = size / 1000000.0;
    return mb < 1 ? `${parseFloat(mb.toFixed(2))} MB` : `${parseFloat(mb.toFixed(1))} MB`;
  }

  scrollToTop() {
    const nav = document.querySelector('nav, header');
    const offset = nav ? nav.offsetHeight + 16 : 16;
    const top = this.element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  show(targetName) {
    const target = this[`has${targetName[0].toUpperCase()}${targetName.slice(1)}Target`] && this[`${targetName}Target`];
    if (target) target.classList.remove('hidden');
  }

  hide(targetName) {
    const target = this[`has${targetName[0].toUpperCase()}${targetName.slice(1)}Target`] && this[`${targetName}Target`];
    if (target) target.classList.add('hidden');
  }

  setFileTile(filename) {
    const ext = (filename.includes('.') ? filename.split('.').pop() : 'FILE').slice(0, 5).toUpperCase();
    this.fileTileTarget.textContent = ext || 'FILE';
  }

  markFileTileReady() {
    this.fileTileTarget.classList.remove('animate-pulse');
  }

  markFileTilePending() {
    this.fileTileTarget.classList.add('animate-pulse');
  }
}
